/**
 * Data service for analyses, companies, dashboard stats, settings, and sales funnel
 */
import api from './api';
import {
  ApiResponse,
  FunnelLead,
  FunnelLeadCreate,
  FunnelLeadUpdate,
  FunnelLeadSummary,
  FunnelKanbanData,
  FunnelStats,
  StageTransition,
  StageHistoryEntry,
  FunnelStage,
  ResourceCatalogEntry,
  ResourceCatalogCreate,
  EngagementKanbanData,
  EngagementStatsResponse,
  EngagementResponse,
  EngagementCreate,
  EngagementStageTransition,
  EngagementStageHistoryEntry,
  AudioUploadResponse,
  EngagementDocumentResponse
} from '../types';

export interface UploadedDocument {
  id: number;
  fileName: string;
  size: number;
  uploadedAt: string;
  downloadUrl?: string;
}

export interface AnalysisRecord {
  id: number;
  companyId: number;
  companyName: string;
  industry?: string;
  fundingStage?: string;
  status: string;
  recommendation: string;
  score: number;
  confidence?: number;
  createdAt: string;
  summary?: string;
  notes?: string;
  ownerEmail?: string;
  documents: UploadedDocument[];
  decisionTier?: string;
  reportMetadata?: {
    overallRecommendation: string;
    overallScore: number;
    confidenceScore: number;
    generatedAt: string;
    analystDomains: string[];
    analystScores: Array<{ domain: string; score: number; confidence: number }>;
    bayesianClaims: number;
    monteCarloMetrics: string[];
  };
}

export interface DashboardStats {
  thisMonth: {
    dealsAnalyzed: number;
    successRate: number;
    avgTurnaround: number;
  };
  recentAnalyses: AnalysisRecord[];
  total: number;
}

export interface PlatformStats {
  investors: {
    total: number;
    researched: number;
    matched: number;
    with_outreach: number;
  };
  companies: {
    total: number;
    researched: number;
    matched: number;
    with_deal: number;
  };
  matches: {
    total: number;
    high_score: number;
    by_status: Record<string, number>;
  };
  deals: {
    total: number;
    active: number;
    by_stage: Record<string, number>;
  };
  generated_at: number;
}

export interface CompanyRecord {
  id: number;
  name: string;
  industry?: string;
  fundingStage?: string;
  status?: string;
  summary?: string;
  description?: string;
  lastAnalysis?: string;
  recommendation?: string;
  score?: number;
  latestAnalysisId?: number;
  documents?: UploadedDocument[];
  analyses?: number[];
  research_completion_pct?: number;
  research_status?: string;
  last_researched_at?: string;
  rubric_score?: number;
  rubric_tier?: string;
  rubric_assessment_at?: string;
  // Partner-export + grouping fields surfaced by the importer.
  pitch_deck_url?: string;
  monthly_revenue?: number;
  previous_funding_raw?: string;
  founder_demographics?: string;
  university_affiliations?: string;
  executive_summary?: string;
  location_state?: string;
  group_name?: string;
}

export interface UserSettings {
  email: string;
  profile: {
    firstName: string;
    lastName: string;
    company: string;
    role: string;
  };
  notifications: {
    weeklyDigest: boolean;
    analysisReady: boolean;
    productUpdates: boolean;
  };
}

class DataService {
  async fetchPlatformStats(): Promise<ApiResponse<PlatformStats>> {
    try {
      const response = await api.get('/dashboard/stats');
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load platform stats' };
    }
  }

  async fetchDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    try {
      const response = await api.get('/dashboard/stats');
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load dashboard stats' };
    }
  }

  async createAnalysis(formData: FormData): Promise<ApiResponse<AnalysisRecord>> {
    try {
      const response = await api.post('/analyses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to submit analysis' };
    }
  }

  async fetchAnalyses(): Promise<ApiResponse<AnalysisRecord[]>> {
    try {
      const response = await api.get('/analyses');
      return { data: response.data?.analyses || [] };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load analyses' };
    }
  }

  async fetchCompanies(opts?: { search?: string; size?: number; min_completion?: number; sort?: string; group?: string; min_revenue?: number; max_revenue?: number }): Promise<ApiResponse<CompanyRecord[]>> {
    try {
      const params: Record<string, any> = {};
      if (opts?.search) params.search = opts.search;
      if (opts?.size) params.size = opts.size;
      if (opts?.min_completion !== undefined) params.min_completion = opts.min_completion;
      if (opts?.sort) params.sort = opts.sort;
      if (opts?.group) params.group = opts.group;
      if (opts?.min_revenue !== undefined) params.min_revenue = opts.min_revenue;
      if (opts?.max_revenue !== undefined) params.max_revenue = opts.max_revenue;
      const response = await api.get('/companies', { params });
      return { data: response.data?.companies || [] };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load companies' };
    }
  }

  async fetchCompanyGroups(): Promise<ApiResponse<Array<{ name: string; count: number }>>> {
    try {
      const response = await api.get('/company-groups');
      return { data: response.data?.groups || [] };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load company groups' };
    }
  }

  async getSettings(email: string): Promise<ApiResponse<UserSettings>> {
    try {
      const response = await api.get('/settings', {
        params: { email }
      });
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load settings' };
    }
  }

  async updateSettings(payload: UserSettings): Promise<ApiResponse<UserSettings>> {
    try {
      const response = await api.put('/settings', {
        email: payload.email,
        profile: payload.profile,
        notifications: payload.notifications
      });
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to update settings' };
    }
  }

  // ============== Sales Funnel Methods ==============

  async fetchFunnelKanban(): Promise<ApiResponse<FunnelKanbanData>> {
    try {
      const response = await api.get('/funnel/leads/kanban');
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load funnel data' };
    }
  }

  async fetchFunnelLeads(params?: {
    page?: number;
    size?: number;
    stage?: FunnelStage;
    search?: string;
  }): Promise<ApiResponse<{ leads: FunnelLead[]; total: number; pages: number }>> {
    try {
      const response = await api.get('/funnel/leads', { params });
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load funnel leads' };
    }
  }

  async fetchFunnelLead(leadId: number): Promise<ApiResponse<FunnelLead>> {
    try {
      const response = await api.get(`/funnel/leads/${leadId}`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load lead' };
    }
  }

  async createFunnelLead(lead: FunnelLeadCreate): Promise<ApiResponse<FunnelLead>> {
    try {
      const response = await api.post('/funnel/leads', lead);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to create lead' };
    }
  }

  async updateFunnelLead(leadId: number, data: FunnelLeadUpdate): Promise<ApiResponse<FunnelLead>> {
    try {
      const response = await api.put(`/funnel/leads/${leadId}`, data);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to update lead' };
    }
  }

  async deleteFunnelLead(leadId: number): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await api.delete(`/funnel/leads/${leadId}`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to delete lead' };
    }
  }

  async transitionLeadStage(leadId: number, transition: StageTransition): Promise<ApiResponse<FunnelLead>> {
    try {
      const response = await api.post(`/funnel/leads/${leadId}/transition`, transition);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to transition lead' };
    }
  }

  async fetchLeadHistory(leadId: number): Promise<ApiResponse<StageHistoryEntry[]>> {
    try {
      const response = await api.get(`/funnel/leads/${leadId}/history`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load lead history' };
    }
  }

  async fetchFunnelStats(): Promise<ApiResponse<FunnelStats>> {
    try {
      const response = await api.get('/funnel/stats');
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load funnel stats' };
    }
  }

  async fetchResources(resourceType?: string): Promise<ApiResponse<ResourceCatalogEntry[]>> {
    try {
      const params = resourceType ? { resource_type: resourceType } : {};
      const response = await api.get('/funnel/resources', { params });
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load resources' };
    }
  }

  async createResource(resource: ResourceCatalogCreate): Promise<ApiResponse<ResourceCatalogEntry>> {
    try {
      const response = await api.post('/funnel/resources', resource);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to create resource' };
    }
  }

  async deleteResource(resourceId: number): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await api.delete(`/funnel/resources/${resourceId}`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to delete resource' };
    }
  }

  // ============== Engagement Pipeline Methods ==============

  async fetchEngagementKanban(): Promise<ApiResponse<EngagementKanbanData>> {
    try {
      const response = await api.get('/engagements/kanban');
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load engagement kanban' };
    }
  }

  async fetchEngagementStats(): Promise<ApiResponse<EngagementStatsResponse>> {
    try {
      const response = await api.get('/engagements/stats');
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load engagement stats' };
    }
  }

  async fetchEngagement(id: number): Promise<ApiResponse<EngagementResponse>> {
    try {
      const response = await api.get(`/engagements/${id}`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load engagement' };
    }
  }

  async createEngagement(data: EngagementCreate): Promise<ApiResponse<EngagementResponse>> {
    try {
      const response = await api.post('/engagements', data);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to create engagement' };
    }
  }

  async updateEngagement(id: number, data: Partial<EngagementResponse>): Promise<ApiResponse<EngagementResponse>> {
    try {
      const response = await api.put(`/engagements/${id}`, data);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to update engagement' };
    }
  }

  async deleteEngagement(id: number): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await api.delete(`/engagements/${id}`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to delete engagement' };
    }
  }

  async transitionEngagementStage(id: number, transition: EngagementStageTransition): Promise<ApiResponse<EngagementResponse>> {
    try {
      const response = await api.post(`/engagements/${id}/transition`, transition);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to transition engagement' };
    }
  }

  async fetchEngagementHistory(id: number): Promise<ApiResponse<EngagementStageHistoryEntry[]>> {
    try {
      const response = await api.get(`/engagements/${id}/history`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load engagement history' };
    }
  }

  async rerunEngagementResearch(id: number): Promise<ApiResponse<EngagementResponse>> {
    try {
      const response = await api.post(`/engagements/${id}/research`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to re-run research' };
    }
  }

  async generateEngagementNDA(id: number, data?: { signer_name?: string; signer_email?: string }): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/engagements/${id}/generate-nda`, data || {});
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to generate NDA' };
    }
  }

  async generateEngagementReport(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/engagements/${id}/generate-report`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to generate report' };
    }
  }

  async sendEngagementReport(id: number, data?: { recipient_email?: string }): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/engagements/${id}/send-report`, data || {});
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to send report' };
    }
  }

  async generateEngagementSummary(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/engagements/${id}/generate-summary`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to generate summary' };
    }
  }

  async generateEngagementContract(id: number, data?: { signer_name?: string; signer_email?: string; contract_value?: number }): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/engagements/${id}/generate-contract`, data || {});
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to generate contract' };
    }
  }

  async uploadEngagementAudio(id: number, file: File): Promise<ApiResponse<AudioUploadResponse>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/engagements/${id}/upload-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to upload audio' };
    }
  }

  async fetchEngagementDocuments(id: number): Promise<ApiResponse<EngagementDocumentResponse[]>> {
    try {
      const response = await api.get(`/engagements/${id}/documents`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || 'Failed to load documents' };
    }
  }
}

const dataService = new DataService();
export default dataService;
